import { PublicationService } from './../../../services/publication.service';
import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { QueryParameter } from './../../../interfaces/queryString';
import { MediaclipService } from 'app/services/mediaclip.service';
import { Location } from '@angular/common';
import { Observable, interval, Subscription, Subject } from 'rxjs';
import { Component, ViewChild, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { SapiEntity, SapiEntityVersion } from '../../../services/sapi.service';
import { AssetService } from '../../../services/asset.service';
import { LiveApiService } from '../../../services/live-api.service';

import { Mediaclip } from '../../../interfaces/mediaclip';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { QuickeditService } from '../../../services/quickedit.service';
import { tap } from 'rxjs/operators';

import { LiveStreamDetails } from '../../../interfaces/live';
import { UploadService } from '../../../services/upload.service';
import { BbTranscodingComponent } from '../../shared/bb-transcoding/bb-transcoding.component';
import { TabItem } from '../../shared/bb-page-header/bb-page-header.component';
import { TranslateService } from '../../../../node_modules/@ngx-translate/core';
import { ModalDefinition } from '../../../services/overlay.service';
import { WebSocketsService } from '../../../services/web-sockets.service';
import { MediaclipDetailsReactComponent } from './mediaclip-details-react.component';

@Component({
	selector: 'mediaclip-details',
	templateUrl: './mediaclip-details.component.html',
	styleUrls: ['./mediaclip-details.component.scss']
})

export class MediaclipDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {

	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild('deleteTemplate') deleteTemplate: TemplateRef<any>;

	@ViewChild(BbTranscodingComponent) transcodingComponent: BbTranscodingComponent;
	@ViewChild(MediaclipDetailsReactComponent) reactComponent: MediaclipDetailsReactComponent;

	activeTab: string;
	sourceUrl: string;
	playerApi: BBPlayerAPI;
	preview: boolean;
	relationInfo: any = {};

	entity: Mediaclip;

	translations: any;

	/** Live API */
	pristineLiveStreamDetails: LiveStreamDetails;
	liveStreamDetails: LiveStreamDetails;
	liveStreamIngestDetails: LiveStreamDetails;
	ingestStatusInterval: Subscription;

	subscriptions = new Subscription();

	_transcodingFinished: boolean;
	uploading: boolean;

	showNoSourceWarning: boolean;

	forcePlayerCreation: boolean;

	isLiveClip: boolean;
	listeners: number;
	icon: string;

	tabItems: TabItem[];

	firstTabItem: TabItem;

	showVersions: boolean;
	rootPermission: boolean;

	constructor(protected route: ActivatedRoute,
		protected assetService: AssetService,
		protected liveApiService: LiveApiService,
		protected quickeditService: QuickeditService,
		protected uploadService: UploadService,
		protected translate: TranslateService,
		protected location: Location,
		private mediaclipService: MediaclipService,
		private publicationService: PublicationService,
		private webSocketsService: WebSocketsService) {
		super(route, location);

		this.entityConfiguration = {
			name: 'commonclip',
			parentPath: '/library/mediaclips',
			basePath: '/library/mediaclip/',
			entityTranslation: 'mediaclip',
			showVersionMetadata: true,
			versioned: true
		};

		this.icon = 'film';
		this.sourceUrl = '';
		this.checklistItems = [];

		this.translations = { username: '', mediatype: '' };

		this.firstTabItem = {
			title: this.translate.instant('METADATA'),
			routerLink: ['/library/mediaclip', 'metadata'],
			icon: 'tags',
		};
		this.authenticationService.hasPermission('root').subscribe((hasRight: boolean) => this.rootPermission = hasRight);
	}

	ngOnInit() {
		super.ngOnInit();

		if (this.isNew) {
			this.tabItems = [this.firstTabItem];
		}

		if (this.entity) {
			this.transcodingFinished = !this.mediaclip.hasRunningJobs && this.mediaclip.transcodingFinished;
			this.uploading = this.mediaclip.uploading;

			if (!this.entity.id) {
				this.checklistItems = [
					{ key: 'assets', label: 'ASSETS', checked: this.mediaclip.assets && this.mediaclip.assets.length > 0 },
					{ key: 'timelines', label: 'INTERACTIVE', checked: this.mediaclip.timelines && this.mediaclip.timelines.length > 0 },
					{ key: 'deeplink', label: 'DEEP_LINK', checked: !!this.mediaclip.deeplink || !!this.mediaclip.gendeeplink },
					{ key: 'status', label: 'PUBLISHED', checked: this.mediaclip.status === 'published' }
				];
			}
		}

		this.subscriptions.add(this.webSocketsService.subscribeToChannel('transcoding').subscribe((websocketEvent: any) => {
			if (websocketEvent.Object && websocketEvent.Object.id === this.entity?.id) {
				if (websocketEvent.ev === 'clipPlayable' && websocketEvent.clipStatus) {
					this.setStatus(websocketEvent.clipStatus);
					this.forcePlayerCreation = true;
				}
			}
		}));
		this.subscriptions.add(this.webSocketsService.subscribeToChannel('MediaClip').subscribe((websocketEvent: any) => {
			if (websocketEvent.payload && websocketEvent.payload.id === this.entity?.id) {
				if (websocketEvent.ev === 'ingest' && websocketEvent.status === 'success') {
					this.showNoSourceWarning = false;
					this.mediaclip.uploading = false;
					if (
						websocketEvent.payload.status
						&& (websocketEvent.useSrcAsAsset || (this.entity.mediatype !== 'video' && this.entity.mediatype !== 'audio'))
					) {
						this.setStatus(websocketEvent.payload.status);
					}
				}
			}
		}));
	}

	setStatus(status: string) {
		this.mediaclip.status = status;
		if (this.mediaclip.status === 'published') {
			const date = new Date();
			const now = date.toISOString();
			this.entity.publisheddate = now;
		}
	}

	thumbnailChange() {
		this.form.form.markAsDirty();
	}

	set mediaclip(mediaclip: Mediaclip) {
		this.mediaclip = mediaclip;
	}

	get mediaclip(): Mediaclip {
		return this.entity;
	}

	get transcodingFinished() {
		return this._transcodingFinished;
	}

	set transcodingFinished(finished: boolean) {
		this.uploading = false;
		if (finished) {
			this._transcodingFinished = true;
			this.forcePlayerCreation = true;
		}
	}

	hasTimeline(): boolean {
		if (this.mediaclip.timelines && this.mediaclip.timelines[0]) {
			return true;
		}
		return false;
	}

	showNoSrcWarning() {
		if (this.mediaclip) {
			if (this.mediaclip.isEmpty) {
				this.showNoSourceWarning = true;
			} else if (this.mediaclip.assets || !this.mediaclip.id) {
				this.showNoSourceWarning = false;
			} else {
				this.showNoSourceWarning = false;
			}
		}
	}

	playerCreated(playerApi: any) {
		setTimeout(() => {
			// timeout to fix ExpressionChangedAfterItHasBeenCheckedError
			this.playerApi = playerApi;
		});
	}

	reloadMediaclip() {
		this.load(this.entity.id);
	}

	// override super delete
	delete(dialogContents?: string): Subject<void> {
		const relations = this.relationInfo.relations = this.mediaclipService.getRelations(this.mediaclip);
		this.relationInfo.defaultContents = this.translate.instant('DELETE_ENTITY_CONFIRM', {
			entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
		});

		if (relations?.project?.length) {
			const queryParams: QueryParameter[] = [{
				name: 'q',
				values: [
					'id:"' + relations.project.join(' OR id:"') + '"'
				]
			}];
			this.sapiService.query('project', queryParams).subscribe((projects) => {
				this.relationInfo.projects = projects.items;
			});
		}
		const deleteObs = super.delete(this.deleteTemplate);
		return deleteObs;
	}

	// override super load
	load(id: string, duplicating?: boolean, version?: SapiEntityVersion): Observable<SapiEntity> {
		const loadObservable = super.load(id, duplicating, version);
		loadObservable.subscribe(() => {
			if (this.mediaclip) {
				this.configureMediaclipTabs();

				if (this.mediaclip.src) {
					this.translations = { username: this.mediaclip.createdBy, mediatype: this.mediaclip.mediatype };
					this.sourceUrl = this.assetService.getAssetUrl(this.mediaclip);
				}

				this.isLiveClip = this.mediaclip.sourceid && this.mediaclip.sourceid.startsWith('live_api_');

				if (!this.isLiveClip) {
					this.checklistItems = [
						{ key: 'assets', label: 'ASSETS', checked: this.mediaclip.assets && this.mediaclip.assets.length > 0 },
						{ key: 'timelines', label: 'INTERACTIVE', checked: this.mediaclip.timelines && this.mediaclip.timelines.length > 0 },
						{ key: 'deeplink', label: 'DEEP_LINK', checked: !!this.mediaclip.deeplink || !!this.mediaclip.gendeeplink },
						{ key: 'status', label: 'PUBLISHED', checked: this.mediaclip.status === 'published' }
					];
				}

				this._transcodingFinished = this.mediaclip.transcodingFinished;
				this.showNoSrcWarning();

				switch (this.mediaclip.mediatype) {
					case 'video': this.icon = 'film';
						break;
					case 'audio': this.icon = 'volume-up';
						break;
					case 'font': this.icon = 'font';
						break;
					case 'document': this.icon = 'file';
						break;
					case 'image': this.icon = 'image';
						break;
					case 'interactive': this.icon = 'cog';
						break;
				}

				this.uploading = false;
				if (this.mediaclip.uploading) {
					this.uploading = this.mediaclip.uploading;
				}

				if (this.mediaclip.sourceid) {
					if (this.isLiveClip) {
						const streamUuid = this.mediaclip.sourceid.split('live_api_')[1];

						this.liveApiService.getStream(streamUuid).subscribe((res: LiveStreamDetails) => {
							this.liveStreamDetails = res; // This object is two-way bound to the fields
							this.liveStreamIngestDetails = res; // This object is read-only to display ingest details
							// This is the pristine copy of the fetch to check what needs to happen after saving
							this.pristineLiveStreamDetails = JSON.parse(JSON.stringify(this.liveStreamDetails));

							this.checklistItems = [
								{ key: 'status', label: 'PUBLISHED', checked: this.mediaclip.status === 'published' },
								{
									key: 'ingesting', label: 'INGESTING',
									checked: this.liveStreamIngestDetails &&
										this.liveStreamIngestDetails.data &&
										this.liveStreamIngestDetails.data.ingest &&
										this.liveStreamIngestDetails.data.ingest.filter((ingestData: any) => ingestData.state === 'ingesting').length > 0
								}
							];

							this.listeners = this.liveStreamIngestDetails &&
								this.liveStreamIngestDetails.data &&
								this.liveStreamIngestDetails.data.ingest &&
								this.liveStreamIngestDetails.data.ingest
									.filter((ingestData: any) => ingestData.details && ingestData.details.hasOwnProperty('listeners'))
									.reduce((listeners: number, ingestData: any) => (listeners || 0) + parseInt(ingestData.details.listeners, 10), null);
						});

						// Start an interval fetch of the live stream details to update the ingest state/details
						this.subscriptions.add(interval(15000).subscribe(() => {
							this.liveApiService.getUrl(`/stream/${streamUuid}`).subscribe((res: LiveStreamDetails) => {
								this.liveStreamIngestDetails = res;

								this.checklistItems = [
									{ key: 'status', label: 'PUBLISHED', checked: this.mediaclip.status === 'published' },
									{
										key: 'ingesting', label: 'INGESTING',
										checked: this.liveStreamIngestDetails &&
											this.liveStreamIngestDetails.data &&
											this.liveStreamIngestDetails.data.ingest &&
											this.liveStreamIngestDetails.data.ingest.filter((ingestData: any) => ingestData.state === 'ingesting').length > 0
									}
								];

								this.listeners = this.liveStreamIngestDetails &&
									this.liveStreamIngestDetails.data &&
									this.liveStreamIngestDetails.data.ingest &&
									this.liveStreamIngestDetails.data.ingest
										.filter((ingestData: any) => ingestData.details && ingestData.details.hasOwnProperty('listeners'))
										.reduce((listeners: number, ingestData: any) => (listeners || 0) + parseInt(ingestData.details.listeners, 10), null);
							});
						}));
					}
				}
			}
		});

		return loadObservable;
	}

	configureMediaclipTabs() {
		const tabItems: TabItem[] = [this.firstTabItem];
		const showImageThumbnailTab = this.publicationService.hasPublicationField('showImageThumbnailTab');

		const audioOrVideoclip = this.mediaclip.mediatype === 'video' || this.mediaclip.mediatype === 'audio';
		const mediaTypeClip = audioOrVideoclip || (this.mediaclip.mediatype === 'image' && showImageThumbnailTab);
		const document = this.mediaclip.mediatype === 'document';
		let liveClip = false;
		let liveApiClip = false;
		let ondemandClip = false;

		if (this.mediaclip.sourcetype) {
			liveClip = this.mediaclip.sourcetype === 'live';
			liveApiClip = this.mediaclip.sourceid && this.mediaclip.sourceid.startsWith('live_api_');
			ondemandClip = this.mediaclip.sourcetype === 'on_demand';
		}

		if (audioOrVideoclip && this.mediaclip.sourcetype !== 'live') {
			tabItems.push({
				title: this.translate.instant('CHAPTERS'),
				routerLink: ['/library/mediaclip', 'chapters'],
				icon: 'list'
			});
		}

		if (!this.isNew && mediaTypeClip) {
			tabItems.push({
				title: this.translate.instant('THUMBNAIL'),
				routerLink: ['/library/mediaclip', 'thumbnail'],
				icon: 'image'
			});
		}

		if (audioOrVideoclip) {
			tabItems.push({
				title: this.translate.instant('FITMODE'),
				routerLink: ['/library/mediaclip', 'fitmode'],
				icon: 'expand'
			});
		}

		if ((audioOrVideoclip || document) && ondemandClip) {
			tabItems.push({
				title: this.translate.instant('RESOURCES'),
				routerLink: ['/library/mediaclip', 'resources'],
				icon: 'sliders'
			});
		}
		// image source tab
		if (this.mediaclip.mediatype === 'image') {
			tabItems.push({
				title: this.translate.instant('SOURCE'),
				routerLink: ['/library/mediaclip', 'source'],
				icon: 'image'
			});
		}

		if (audioOrVideoclip) {

			if (liveClip) {
				if (liveApiClip) {
					tabItems.push({
						title: this.translate.instant('STREAMS'),
						routerLink: ['/library/mediaclip', 'bb-streams'],
						icon: 'feed'
					});
				} else {
					tabItems.push({
						title: this.translate.instant('STREAMS'),
						routerLink: ['/library/mediaclip', 'streams'],
						icon: 'feed'
					});
				}
			}

			if (ondemandClip) {
				tabItems.push({
					title: this.translate.instant('LANGUAGES'),
					routerLink: ['/library/mediaclip', 'languages'],
					icon: 'language'
				});
			}

			tabItems.push({
				title: this.translate.instant('EMBED'),
				routerLink: ['/library/mediaclip', 'embed'],
				icon: 'code'
			});
		}

		if (this.mediaclip.mediatype === 'video') {
			if (ondemandClip) {
				tabItems.push({
					title: this.translate.instant('SOCIAL'),
					routerLink: ['/library/mediaclip', 'social'],
					icon: 'share-alt'
				});
			} else if (liveClip && liveApiClip) {
				tabItems.push({
					title: this.translate.instant('RESTREAMING'),
					routerLink: ['/library/mediaclip', 'restreaming'],
					icon: 'share-alt'
				});
			}
			tabItems.push({
				title: this.translate.instant('REACT'),
				routerLink: ['/library/mediaclip', 'react'],
				icon: 'comments-o'
			});
		}

		this.tabItems = tabItems;
	}

	goToResources() {
		this.router.navigate(['library/mediaclip', this.mediaclip.id, 'resources']);
	}

	cancel(): Subject<SapiEntity> {
		// note: react data is stored seperately
		if (this.activeTab === 'react') {
			this.reactComponent.cancel();
		}
		return super.cancel();
	}

	save(force: boolean): Observable<any> {
		this.liveApiService.saveStream(this.pristineLiveStreamDetails, this.liveStreamDetails).subscribe();

		const quickEditSubject = this.quickeditService.save();

		quickEditSubject.pipe(
			tap(() => super.save(force))
		)
			.subscribe();

		return quickEditSubject;
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		this.webSocketsService.unsubscribeFromChannel('transcoding');
	}
}
